import { Locale, RedisTtlConfig } from '@headless-workspace/config';
import {
    AuthorizationStrategy,
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
    QueryParamStrategy,
    RefreshTokenStrategy,
} from '@headless-workspace/data';
import { CachingHelpers } from '@headless-workspace/utils';
import { ConfigByKeyContainerDTO, Result, ResultEmpty, ResultFailureFromApiError, ResultSuccess } from '../../../src';
import { CachedRepository } from '../types/CachedRepository';

export interface ConfigByKeyRepositorySpec {
    getConfigByKey<T>(key: string, params: Record<string, unknown>, decoder: (value: unknown) => T): Promise<Result<T>>;
}

const CACHE_SERVICE_CONFIG_KEY = 'config';

export abstract class ConfigByKeyRepository extends CachedRepository implements ConfigByKeyRepositorySpec {
    path = '/settings/v1';

    constructor(
        readonly authStrategy: Partial<AuthorizationStrategy> = memoryCacheAuthStrategy,
        readonly refreshTokenStrategy: Partial<RefreshTokenStrategy> = memoryCacheRefreshTokenStrategy,
        queryParamStrategy: Partial<QueryParamStrategy> = memoryCacheQueryParamStrategy,
    ) {
        super(authStrategy, refreshTokenStrategy, queryParamStrategy);
    }

    async getConfigByKey<T>(
        key: string,
        params: Record<string, unknown>,
        decoder: (value: unknown) => T,
    ): Promise<Result<T>> {
        // Get locale from params - all config by key repository provide locale as param
        const locale = params['locale'] as Locale;

        return this.withCache(
            locale,
            CACHE_SERVICE_CONFIG_KEY,
            CachingHelpers.createCacheKey(key, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(CACHE_SERVICE_CONFIG_KEY, key, params),
            },
            () => this.fetchConfigByKey(key, params, decoder),
        );
    }

    private async fetchConfigByKey<T>(
        key: string,
        params: Record<string, unknown>,
        decoder: (value: unknown) => T,
    ): Promise<Result<T>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/configs/${key}`, params);
            const config = ConfigByKeyContainerDTO.parse(response);
            const value = decoder(config.value);

            if (value === undefined || value === null) {
                return ResultEmpty();
            }
            if (Array.isArray(value) && value.length === 0) {
                return ResultEmpty();
            }
            if (typeof value === 'string' && value === '') {
                return ResultEmpty();
            }
            if (typeof value === 'object' && Object.keys(value).length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(value);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { ...params });
        }
    }

    // Cache invalidation helpers
    async invalidateConfigByKey(locale: Locale, key: string, params?: Record<string, unknown>): Promise<void> {
        const tags = CachingHelpers.createTags(CACHE_SERVICE_CONFIG_KEY, key, params ?? {});
        await this.invalidateCache(locale, tags);
    }

    async invalidateAllConfigs(locale: Locale): Promise<void> {
        await this.invalidateCache(locale, [CACHE_SERVICE_CONFIG_KEY]);
    }
}
